import React, { ReactElement } from 'react';

import { Control, FieldValues, Path } from 'react-hook-form';
import { ReadOnlyInput, type ReadOnlyInputProps } from '@infoods/lifebase-ui';
import { Mode, Option } from '../../../../general-types';

/** Props only for view mode (passed to ReadOnlyInput) */
type ViewOnlyProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> = Pick<
  ReadOnlyInputProps<TFieldValues, TName>,
  'placeholder' | 'renderValue' | 'valueClassName'
>;

type ModeAwareInputProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
  TOptions extends Option[] | undefined,
> = {
  mode: Mode;
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  options?: TOptions; // optional prop - infers TOptions from what's passed
  readonly?: boolean;
  size?: 'small' | 'medium';
  /** Render callback for edit mode. Receives shared props with inferred options type */
  renderEdit: (props: {
    control: Control<TFieldValues>;
    name: TName;
    label?: string;
    options: TOptions;
    size?: 'small' | 'medium';
  }) => ReactElement;
} & ViewOnlyProps<TFieldValues, TName>;

/**
 * Wrapper component for form inputs that switches between view and edit modes.
 * In VIEW mode renders ReadOnlyInput, in EDIT mode calls renderEdit callback.
 * Options type flows through: if passed, renderEdit gets Option[]; if not, gets undefined.
 */
export const ModeAwareInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
  TOptions extends Option[] | undefined = undefined,
>({
  mode,
  control,
  name,
  label,
  options,
  placeholder = '—',
  readonly,
  renderValue,
  valueClassName,
  renderEdit,
  size,
}: ModeAwareInputProps<TFieldValues, TName, TOptions>) => {
  const sharedProps = {
    control,
    name,
    label,
    options: options as TOptions,
    // only when passed: consumers spread their own `size` before these props
    ...(size && { size }),
  };

  if (mode === 'VIEW' || readonly) {
    return (
      <ReadOnlyInput
        {...sharedProps}
        placeholder={placeholder}
        renderValue={renderValue}
        valueClassName={valueClassName}
      />
    );
  }

  return renderEdit(sharedProps);
};
